import * as React from 'react';

import { ResearchGraphic, type ResearchGraphicName } from '@/components/research-graphic';
import { useDocumentTitle } from './zone';

/**
 * The Advisor Portal's one signature: the guidelines' approved Royal Blue → Sapphire gradient,
 * white type on it, and one analysis graphic cropped off the right edge (guidelines p.5 and
 * p.10–11). Home opens with it and no other screen does — every other portal page opens with the
 * light `PageHeader` — so the gradient keeps meaning "this is where you start".
 *
 * The title is the page's `h1`. The small line above it is content, not a label: the cycle the
 * page is about.
 */
export function Hero({
  context,
  title,
  documentTitle,
  lede,
  action,
  graphic = 'rising-benchmarks'
}: {
  context?: React.ReactNode;
  title: React.ReactNode;
  documentTitle: string;
  lede?: React.ReactNode;
  action?: React.ReactNode;
  graphic?: ResearchGraphicName;
}) {
  useDocumentTitle(documentTitle);
  return (
    <section className="veritant-gradient-royal-sapphire relative overflow-hidden">
      <ResearchGraphic
        name={graphic}
        className="pointer-events-none absolute top-[-7.5rem] right-[4%] hidden h-[32rem] w-[32rem] text-[var(--color-bright-blue)] opacity-35 md:block"
      />
      <div className="relative mx-auto w-full max-w-[1120px] px-6 pt-14 pb-16 lg:px-10">
        {context && <p className="text-[0.9375rem] text-[var(--color-slight-blue)]">{context}</p>}
        <h1 className="mt-2 max-w-[16em] text-[2.25rem] leading-[1.12] font-light tracking-[-0.01em] text-white sm:text-[2.75rem]">
          {title}
        </h1>
        {lede && (
          <p className="mt-4 max-w-[34em] text-[1.0625rem] leading-relaxed text-[var(--color-slight-blue)]">
            {lede}
          </p>
        )}
        {action && <div className="mt-7 flex flex-wrap items-center gap-3">{action}</div>}
      </div>
    </section>
  );
}
